import React from "react";
import { Box, Typography, TextField, Button } from "@material-ui/core";
import { generatePredicate } from "../../api/generatePredicate";
import { editorStyles as styles } from "./editorStyles";

type AiTarget = {
  questionText: string;
  questionId: number;
  entityType: "question" | "question_group";
  kind: "visibility" | "requirement";
};

type Message = { role: "user" | "assistant"; text: string };

type Props = {
  entityLabel: string;
  aiTarget: AiTarget | null;
  input: string;
  onInputChange: (value: string) => void;
  feelIdToText: Record<string, string>;
  optionFeelIdToText: Record<string, string>;
  onApply: (predicate: string) => void;
};

const AiChatPanel: React.VFC<Props> = ({ entityLabel, aiTarget, input, onInputChange, feelIdToText, optionFeelIdToText, onApply }) => {
  const [messages, setMessages] = React.useState<Message[]>([]);
  const [loading, setLoading] = React.useState(false);
  const endRef = React.useRef<HTMLDivElement>(null);

  const scrollToEnd = () => setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);

  const send = async (text: string) => {
    if (!text || !aiTarget) return;
    setMessages((prev) => [...prev, { role: "user", text }]);
    onInputChange("");
    setLoading(true);
    scrollToEnd();
    try {
      const predicate = await generatePredicate({
        question_text: aiTarget.questionText,
        question_id: aiTarget.questionId,
        entity_type: aiTarget.entityType,
        predicate_kind: aiTarget.kind,
        feel_id_to_text: feelIdToText,
        option_feel_id_to_text: optionFeelIdToText,
        prompt: text,
      });
      setMessages((prev) => [...prev, { role: "assistant", text: predicate }]);
    } catch {
      setMessages((prev) => [...prev, { role: "assistant", text: "Error generating predicate. Please try again." }]);
    } finally {
      setLoading(false);
      scrollToEnd();
    }
  };

  return (
    <div className={styles.form} style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box flex={1} display="flex" flexDirection="column" p={2} style={{ gap: 8, overflowY: "auto" }}>
        {messages.length === 0 ? (
          <Box flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center" style={{ gap: 16 }}>
            <Typography variant="h5" style={{ opacity: 0.5 }}>🤖 AI Chat Editor</Typography>
            <Typography variant="body1" color="textSecondary" align="center">
              Chat with AI to edit your {entityLabel}
            </Typography>
          </Box>
        ) : (
          messages.map((msg, i) => (
            <Box key={i} style={{ alignSelf: msg.role === "user" ? "flex-end" : "flex-start", background: msg.role === "user" ? "#1976d2" : "#f5f5f5", color: msg.role === "user" ? "#fff" : "#333", padding: "8px 12px", borderRadius: 8, maxWidth: "80%", wordBreak: "break-word" }}>
              <Typography variant="body2">{msg.text}</Typography>
              {msg.role === "assistant" && aiTarget && (
                <Box mt={1} display="flex" justifyContent="flex-end">
                  <Button size="small" variant="contained" color="primary" onClick={() => onApply(msg.text)}>
                    Apply
                  </Button>
                </Box>
              )}
            </Box>
          ))
        )}
        <div ref={endRef} />
      </Box>
      <Box p={2} borderTop="1px solid #e0e0e0" display="flex" alignItems="flex-end" style={{ gap: 8 }}>
        <TextField
          fullWidth
          multiline
          minRows={1}
          maxRows={6}
          size="small"
          variant="outlined"
          inputProps={{ style: { fontSize: 15 } }}
          placeholder={`Ask me to modify the ${entityLabel}...`}
          value={input}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && input.trim()) {
              e.preventDefault();
              send(input.trim());
            }
          }}
        />
        <Button size="small" variant="contained" color="primary" disabled={!input.trim() || loading} onClick={() => send(input.trim())}>
          {loading ? "..." : "Send"}
        </Button>
      </Box>
    </div>
  );
};

export default AiChatPanel;
